import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Container,
  Card,
  TextField,
  Alert,
  Snackbar
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/auth.store';
import { useSupportStore } from '../../../store/support.store'; // 🚀 Import the support store
import DataPrivacy from './DataPrivacy';
import Testimonials from './Testimonials';
import RegisterDialog from './RegisterDialog';
import FAQDialog from './FAQDialog';
import ContactDialog from './ContactDialog';
import DemoTourDialog from './DemoTourDialog';

export default function HomePage() {
  const navigate = useNavigate();

  const { login, register, loading, isAuthenticated } = useAuthStore();
  const { submitContactForm, loading: contactLoading } = useSupportStore(); // 🚀 Pull support hooks

  const testimonials = [
    {
      name: 'Arun',
      review: 'Novex Insights completely changed how I track my ASX portfolio.'
    },
    {
      name: 'Priya',
      review:
        'The tax reporting is unbelievably accurate. I exported my FY report in seconds.'
    },
    {
      name: 'Michael',
      review: 'Clean UI, fast performance, and the insights are spot on.'
    }
  ];

  // Dialog states
  const [registerOpen, setRegisterOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  // Success Notification Toast states
  const [successToastOpen, setSuccessToastOpen] = useState(false);
  const [contactToastOpen, setContactToastOpen] = useState(false); // 🚀 Success toast state for contacts

  // Isolate validation and backend API errors locally
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null); // 🚀 Contact error state

  // Login fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Automatically forward the session once authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/management/holdings');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    setLoginError(null);
    try {
      await login(email, password);
    } catch (err: any) {
      setLoginError(
        err?.response?.data?.message ||
          'Login failed. Please check your credentials.'
      );
    }
  };

  const handleRegistrationSubmit = async (data: {
    first: string;
    last: string;
    email: string;
    pass: string;
    acceptedTerms: boolean;
  }) => {
    setRegisterError(null);
    try {
      await register({
        firstName: data.first,
        lastName: data.last,
        email: data.email,
        password: data.pass,
        acceptedTerms: data.acceptedTerms
      });

      setSuccessToastOpen(true);
      setRegisterOpen(false);
    } catch (err: any) {
      setRegisterError(
        err?.response?.data?.message || 'Registration failed. Please try again.'
      );
    }
  };

  // 🚀 Contact form handler matching registration submission pipeline
  const handleContactSubmit = async (data: {
    name: string;
    email: string;
    message: string;
  }) => {
    setContactError(null);
    try {
      await submitContactForm(data);
      setContactToastOpen(true);
      setContactOpen(false);
    } catch (err: any) {
      setContactError(
        err?.response?.data?.message ||
          'Failed to submit contact message. Please try again.'
      );
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <AppBar position="static" color="inherit" elevation={0}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <img
              src="/novex-insights.png"
              alt="logo"
              style={{ height: 25, marginRight: 10, marginLeft: 10 }}
            />
          </Box>

          <Box>
            <Button color="inherit" onClick={() => navigate('/')}>
              Home
            </Button>
            <Button color="inherit" onClick={() => setFaqOpen(true)}>
              FAQ
            </Button>
            <Button
              color="inherit"
              onClick={() => {
                setContactError(null);
                setContactOpen(true);
              }}
            >
              Contact
            </Button>
            <Button color="inherit" onClick={() => setTourOpen(true)}>
              Demo Tour
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        {/* ... (Sign In Form Markup stays unchanged) ... */}
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          py={12}
          gap={4}
        >
          <Box maxWidth={600}>
            <Box mb={4}>
              <img
                src="/novex-insights.png"
                alt="Novex Insight"
                style={{ height: '120px', width: 'auto' }}
              />
            </Box>

            <Typography
              variant="h3"
              fontWeight={700}
              mb={2}
              color="primary.main"
            >
              Smarter Insights for Your Portfolio
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              mb={4}
              sx={{ lineHeight: 1.6 }}
            >
              Novex Insights helps you track holdings, analyse performance,
              manage corporate actions, and generate tax-ready reports — all in
              one beautifully designed dashboard.
            </Typography>
          </Box>

          <Card
            sx={{
              p: 5,
              width: { xs: '100%', sm: 420 },
              boxShadow: '0px 10px 30px rgba(0,0,0,0.05)',
              borderRadius: 3
            }}
          >
            <Typography variant="h5" mb={3} textAlign="center" fontWeight={600}>
              Sign in to Novex Insights
            </Typography>

            {loginError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {loginError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <TextField
              fullWidth
              type="password"
              label="Password"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>

            <Typography variant="body2" mt={2} textAlign="center">
              Don’t have an account?{' '}
              <Button
                size="small"
                onClick={() => {
                  setRegisterError(null);
                  setRegisterOpen(true);
                }}
              >
                Register
              </Button>
            </Typography>
          </Card>
        </Box>

        <Box
          component="section"
          aria-labelledby="features-heading"
          py={{ xs: 6, md: 10 }}
        >
          <Typography
            id="features-heading"
            variant="h4"
            textAlign="center"
            fontWeight={700}
            mb={2}
          >
            A clearer view of every investment decision
          </Typography>
          <Typography
            textAlign="center"
            color="text.secondary"
            maxWidth={680}
            mx="auto"
            mb={5}
          >
            Bring your portfolio records together and spend less time piecing
            together statements, transactions and tax information.
          </Typography>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }}
            gap={3}
          >
            {[
              {
                icon: (
                  <AccountBalanceWalletOutlinedIcon
                    color="primary"
                    fontSize="large"
                  />
                ),
                title: 'Holdings in one place',
                text: 'See quantities, cost basis, average prices and portfolio value across your investments.'
              },
              {
                icon: (
                  <AutoGraphOutlinedIcon color="primary" fontSize="large" />
                ),
                title: 'Transaction-aware insights',
                text: 'Track buys, sells, dividends and DRP activity so your portfolio reflects what actually happened.'
              },
              {
                icon: (
                  <ReceiptLongOutlinedIcon color="primary" fontSize="large" />
                ),
                title: 'Tax-lot tracking',
                text: 'Follow tax lots and FIFO disposals to make realised gains easier to review at tax time.'
              },
              {
                icon: (
                  <AssessmentOutlinedIcon color="primary" fontSize="large" />
                ),
                title: 'Reports that explain the numbers',
                text: 'Generate practical transaction and realised-gain reports for your own records and review.'
              }
            ].map((feature) => (
              <Box
                key={feature.title}
                sx={{
                  p: 3,
                  backgroundColor: 'common.white',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                {feature.icon}
                <Typography variant="h6" fontWeight={700} mt={2} mb={1}>
                  {feature.title}
                </Typography>
                <Typography color="text.secondary" lineHeight={1.7}>
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          component="section"
          aria-labelledby="workflow-heading"
          py={{ xs: 6, md: 10 }}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Typography
            id="workflow-heading"
            variant="h4"
            fontWeight={700}
            mb={4}
          >
            From broker activity to portfolio clarity
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, 1fr)' }}
            gap={4}
          >
            {[
              [
                '01',
                'Connect your records',
                'Import supported broker transaction files or forward CommSec confirmations to your Novex email address.'
              ],
              [
                '02',
                'Review the activity',
                'Check imported transactions, holdings and corporate actions in one workspace.'
              ],
              [
                '03',
                'Understand your position',
                'Use tax-lot and realised-gain reporting to prepare for informed conversations and decisions.'
              ]
            ].map(([number, title, text]) => (
              <Box key={number}>
                <Typography color="primary.main" fontWeight={800} variant="h5">
                  {number}
                </Typography>
                <Typography variant="h6" fontWeight={700} mt={1} mb={1}>
                  {title}
                </Typography>
                <Typography color="text.secondary" lineHeight={1.7}>
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      <DataPrivacy />
      <Testimonials testimonials={testimonials} />

      <RegisterDialog
        open={registerOpen}
        onClose={() => {
          setRegisterOpen(false);
          setRegisterError(null);
        }}
        onSubmit={handleRegistrationSubmit}
        loading={loading}
        error={registerError}
      />

      {/* 🚀 Updated Contact Dialog with isolated state variables */}
      <ContactDialog
        open={contactOpen}
        onClose={() => {
          setContactOpen(false);
          setContactError(null);
        }}
        onSubmitContact={handleContactSubmit}
        loading={contactLoading}
        error={contactError}
      />

      {/* 🚀 Contact Success Notification Toast */}
      <Snackbar
        open={contactToastOpen}
        autoHideDuration={4000}
        onClose={() => setContactToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setContactToastOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 500 }}
        >
          Message sent successfully! <br />
          Our support team will get back to you shortly.
        </Alert>
      </Snackbar>

      {/* Registration Success Toast */}
      <Snackbar
        open={successToastOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccessToastOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 500 }}
        >
          Account Registered successfully! <br />
          Verify your email to activate your workspace session.
        </Alert>
      </Snackbar>

      {/* 🚀 Contact Success Toast */}
      <Snackbar
        open={contactToastOpen}
        autoHideDuration={4000}
        onClose={() => setContactToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setContactToastOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 500 }}
        >
          Message sent successfully! <br />
          Our support team will get back to you shortly.
        </Alert>
      </Snackbar>

      <FAQDialog open={faqOpen} onClose={() => setFaqOpen(false)} />
      <DemoTourDialog open={tourOpen} onClose={() => setTourOpen(false)} />

      <Box
        textAlign="center"
        py={4}
        bgcolor="#ffffff"
        borderTop="1px solid #eee"
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Novex Insights — support@novexinsight.com
        </Typography>
      </Box>
    </Box>
  );
}
